import React from 'react';
import Modal from 'react-modal';
import '../SettingsModalContent.scss';
import { autoId, useReactModal } from '@workday/upc-common';
import './contact-info-settings-row.scss';
import { AddAliasModalSteps } from './add-alias/add-alias-modal-steps';
import { EditAliasesModalContent } from './edit-aliases/edit-aliases-modal-content';
import { ManagePhoneNumbersModalSteps } from './manage-phone-numbers/ManagePhoneNumbersModalSteps';
import { ID_ADD_EMAIL_LINK, ID_EDIT_EMAIL_LINK, ID_EDIT_PHONE_LINK, ID_PHONE_NUMBER } from '../../../autoIds';
import { StatusIndicatorEmphasis, StatusIndicatorType, LoadingDots, Hyperlink } from '@workday/canvas-kit-react';
import parsePhoneNumber from 'libphonenumber-js';
import { useFetchUserAliasesQuery } from '../../../slices/account-api.slice';
import { sortArray } from '../../../utils/array';
import { useTranslation } from 'react-i18next';
import { WclStatusIndicator } from '../../../shared/indicator/WclStatusIndicator';

export function ContactInfoSettingsRow({ userInfo }) {
    const { data, isLoading } = useFetchUserAliasesQuery();

    const emailAliases = data?.data || [];

    const addModal = useReactModal();
    const editModal = useReactModal();
    const editPhoneModal = useReactModal();

    const formatPhoneNumber = str => {
        const phoneNumber = parsePhoneNumber(str);
        // @ts-ignore
        return `+${phoneNumber.countryCallingCode} ${phoneNumber.formatNational({ nationalPrefix: false })}`;
    };

    const { t } = useTranslation('account');

    const sortedEmailAliases = sortArray(emailAliases, (a, b) =>
        a.primary ? -1 : b.primary ? 1 : a.alias.localeCompare(b.alias)
    );

    return (
        <div className='settingsRow'>
            <h1 className='settingsRow__title'>{t('ContactInfoSettingsRow.rowTitle')}</h1>
            <div className='settingsRow__content'>
                {isLoading ? (
                    <LoadingDots className='loadingContacts' />
                ) : (
                    <div className='contactInfoSettingsRow'>
                        <div className='titleCell'>{t('ContactInfoSettingsRow.emailTitle')}</div>
                        <div className='editCell'>
                            <Hyperlink as={'button'} {...autoId(ID_EDIT_EMAIL_LINK)} onClick={editModal.open}>
                                {t('ContactInfoSettingsRow.showEditEmailModal')}
                            </Hyperlink>
                        </div>
                        <div className='contentCell'>
                            {sortedEmailAliases.map(emailAlias => (
                                <div className='contentRow' key={emailAlias.alias}>
                                    <div className='contentRowColumn'>
                                        <span>{emailAlias.alias}</span>
                                        {emailAlias.primary && (
                                            <WclStatusIndicator
                                                className='primaryLabel'
                                                type={StatusIndicatorType.Blue}
                                                label={t('ContactInfoSettingsRow.primaryLabel')}
                                                emphasis={StatusIndicatorEmphasis.Low}
                                            />
                                        )}
                                    </div>
                                    <WclStatusIndicator
                                        className='verifiedLabel'
                                        type={StatusIndicatorType.Green}
                                        label={t('ContactInfoSettingsRow.verifiedLabel')}
                                        emphasis={StatusIndicatorEmphasis.Low}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className='addCell'>
                            <Hyperlink as={'button'} {...autoId(ID_ADD_EMAIL_LINK)} onClick={addModal.open}>
                                + {t('ContactInfoSettingsRow.addEmailModal')}
                            </Hyperlink>
                        </div>
                    </div>
                )}
                <div className='contactInfoSettingsRow'>
                    <div className='titleCell'>{t('ContactInfoSettingsRow.mobileTitle')}</div>
                    <div className='editCell'>
                        <Hyperlink as={'button'} {...autoId(ID_EDIT_PHONE_LINK)} onClick={editPhoneModal.open}>
                            {userInfo?.phoneNumber
                                ? t('ContactInfoSettingsRow.showEditPhoneModal')
                                : t('ContactInfoSettingsRow.showAddPhoneModal')}
                        </Hyperlink>
                    </div>
                    <div className='contentCell'>
                        {userInfo?.phoneNumber ? (
                            <div className='contentRow'>
                                <span {...autoId(ID_PHONE_NUMBER)}>{formatPhoneNumber(userInfo.phoneNumber)}</span>
                                <WclStatusIndicator
                                    className={`verifiedLabel ${!userInfo.isPhoneVerified && 'unverified'}`}
                                    type={
                                        userInfo.isPhoneVerified ? StatusIndicatorType.Green : StatusIndicatorType.Gray
                                    }
                                    label={
                                        userInfo.isPhoneVerified
                                            ? t('ContactInfoSettingsRow.verifiedLabel')
                                            : t('ContactInfoSettingsRow.unverifiedLabel')
                                    }
                                    emphasis={StatusIndicatorEmphasis.Low}
                                />
                            </div>
                        ) : null}
                    </div>
                    <div className='addCell'>{/* TODO add phone modal button */}</div>
                </div>
                {addModal.isOpen ? (
                    <Modal {...addModal.modalProps}>
                        <AddAliasModalSteps aliases={emailAliases} onClose={addModal.close} />
                    </Modal>
                ) : null}
                {editModal.isOpen ? (
                    <Modal {...editModal.modalProps}>
                        <EditAliasesModalContent emailAliases={emailAliases} onClose={editModal.close} />
                    </Modal>
                ) : null}
                {editPhoneModal.isOpen ? (
                    <Modal {...editPhoneModal.modalProps}>
                        <ManagePhoneNumbersModalSteps userInfo={userInfo} handleDone={editPhoneModal.close} />
                    </Modal>
                ) : null}
            </div>
        </div>
    );
}
